import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    List,
    ListItemButton,
    MenuItem,
    Select,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
    Chip,
} from '@mui/material';
import { useGetList } from 'react-admin';
import { DialogCloseButton } from '@/components/misc/DialogCloseButton';
import { formatDate } from '@/utils/date_utils';

import type { ExpenseTransaction, Receipt, ResponsibleUser } from './receiptApi';
import { ReceiptFilePreview } from './ReceiptFilePreview';
import {
    amountMatches,
    rankReceiptsForTransaction,
    receiptMatchIssues,
} from './receiptHelpers';

type Props = {
    transaction: ExpenseTransaction;
    users: ResponsibleUser[];
    busy: boolean;
    onClose: () => void;
    /** When omitted, the responsible-employee controls are hidden (e.g. My expenses). */
    onAssign?: (transactionId: number, userId: number | null) => Promise<boolean>;
    onMatch: (transactionId: number, receiptId: number) => Promise<boolean>;
    /** Extra receipt list filters merged with unmatched + not-deleted. */
    receiptFilter?: Record<string, unknown>;
};

export const ReceiptMatchDialog = ({
    transaction,
    users,
    busy,
    onClose,
    onAssign,
    onMatch,
    receiptFilter,
}: Props) => {
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('md'));
    const canAssign = !!onAssign;
    const [responsibleUserId, setResponsibleUserId] = useState<number | ''>(
        transaction.responsible_user_id ?? ''
    );
    const [savedUserId, setSavedUserId] = useState<number | null>(
        transaction.responsible_user_id ?? null
    );
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);
    const mountedRef = useRef(true);

    // Fetch unmatched receipts
    const { data: receipts = [], isPending, error, total } = useGetList<Receipt>(
        'receipts',
        {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'id', order: 'DESC' },
            filter: {
                status: 'unmatched',
                deleted_at: null,
                ...receiptFilter,
            },
        }
    );

    const ranked = useMemo(
        () => rankReceiptsForTransaction(receipts, transaction),
        [receipts, transaction]
    );

    const selected = ranked.find(r => r.id === selectedId);

    const userName = useMemo(() => {
        const byId = new Map(
            users.map(user => [user.id, user.name || user.email] as const)
        );
        return (id: number | null) =>
            id == null ? 'Unassigned' : byId.get(id) || `User #${id}`;
    }, [users]);

    // Track unmount to prevent setState after component unmounts
    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const handleMatch = async () => {
        if (!selected) return;
        setSaving(true);
        try {
            if (canAssign && onAssign) {
                const newUser =
                    responsibleUserId === '' ? null : Number(responsibleUserId);
                if (newUser !== savedUserId) {
                    if (!(await onAssign(transaction.id, newUser))) return;
                }
            }
            if (await onMatch(transaction.id, selected.id)) {
                onClose();
            }
        } finally {
            if (mountedRef.current) setSaving(false);
        }
    };

    const handleSaveEmployee = async () => {
        if (!onAssign) return;
        const newUser = responsibleUserId === '' ? null : Number(responsibleUserId);
        setSaving(true);
        try {
            if (await onAssign(transaction.id, newUser)) {
                if (mountedRef.current) setSavedUserId(newUser);
            }
        } finally {
            if (mountedRef.current) setSaving(false);
        }
    };

    const userChanged = canAssign && responsibleUserId !== savedUserId;
    const showSaveEmployee = canAssign && !selected && userChanged;
    const showMatch = !!selected;

    return (
        <Dialog
            open
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            fullScreen={isSmall}
        >
            <DialogTitle sx={{ pr: 6 }}>
                Match receipt
                <DialogCloseButton onClose={onClose} />
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Transaction Summary */}
                <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                        {transaction.description} · ${transaction.amount} ·{' '}
                        {transaction.source_network}
                        {transaction.card_last4 ? ` ••••${transaction.card_last4}` : ''}{' '}
                        · {transaction.transaction_date}
                    </Typography>
                </Stack>

                {/* Responsible Employee Select (admin reconciliation only) */}
                {canAssign && (
                    <FormControl size="small" sx={{ maxWidth: 300 }}>
                        <InputLabel>Responsible employee</InputLabel>
                        <Select
                            value={responsibleUserId}
                            label="Responsible employee"
                            disabled={busy || saving}
                            onChange={event =>
                                setResponsibleUserId(
                                    event.target.value
                                        ? Number(event.target.value)
                                        : ''
                                )
                            }
                        >
                            <MenuItem value="">Unassigned</MenuItem>
                            {users.map(user => (
                                <MenuItem key={user.id} value={user.id}>
                                    {user.name || user.email}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {/* Receipts List and Preview */}
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    sx={{ flex: 1, minHeight: { md: '65vh' } }}
                >
                    {/* Left Column: Receipts List */}
                    <Box
                        sx={{
                            flex: { md: '0 0 40%' },
                            maxHeight: { md: '65vh' },
                            overflowY: 'auto',
                        }}
                    >
                        {isPending ? (
                            <Typography color="text.secondary">
                                Loading receipts...
                            </Typography>
                        ) : error ? (
                            <Alert severity="error">{error.message}</Alert>
                        ) : !ranked.length ? (
                            <Typography color="text.secondary">
                                No unmatched receipts.
                            </Typography>
                        ) : (
                            <>
                                <List disablePadding>
                                    {ranked.map(receipt => (
                                        <ListItemButton
                                            key={receipt.id}
                                            selected={receipt.id === selectedId}
                                            onClick={() => setSelectedId(receipt.id)}
                                            sx={{
                                                py: 1.5,
                                                borderBottom: 1,
                                                borderColor: 'divider',
                                                minHeight: '44px',
                                                flexDirection: 'column',
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                                sx={{ width: '100%' }}
                                            >
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={600}
                                                    >
                                                        {receipt.merchant ||
                                                            receipt.original_filename}{' '}
                                                        {receipt.amount
                                                            ? `· $${receipt.amount}`
                                                            : ''}
                                                    </Typography>
                                                </Box>
                                                {amountMatches(receipt, transaction) && (
                                                    <Chip
                                                        size="small"
                                                        color="success"
                                                        label="Amount matches"
                                                    />
                                                )}
                                            </Stack>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {receipt.transaction_date ||
                                                    'No purchase date'}{' '}
                                                · {userName(receipt.responsible_user_id)}
                                            </Typography>
                                        </ListItemButton>
                                    ))}
                                </List>
                                {total != null && total > 100 && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mt: 1 }}
                                    >
                                        Showing the first 100 of {total} unmatched
                                        receipts
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>

                    {/* Right Column: Preview and Info */}
                    <Box sx={{ flex: { md: 1 } }}>
                        {!selected ? (
                            <Typography color="text.secondary">
                                Select a receipt to preview it.
                            </Typography>
                        ) : (
                            <Stack spacing={2}>
                                <ReceiptFilePreview key={selected.id} receipt={selected} />

                                {/* Receipt Info */}
                                <Stack spacing={1}>
                                    <InfoRow
                                        label="Merchant"
                                        value={selected.merchant || 'N/A'}
                                    />
                                    <InfoRow
                                        label="Amount"
                                        value={
                                            selected.amount
                                                ? `$${selected.amount}`
                                                : 'N/A'
                                        }
                                    />
                                    <InfoRow
                                        label="Purchase date"
                                        value={selected.transaction_date || 'N/A'}
                                    />
                                    <InfoRow
                                        label="Allocation"
                                        value={
                                            selected.allocation_type ===
                                            'project_cost'
                                                ? `Project cost (COGS) · Project #${selected.project_no ?? selected.project_id ?? '—'}`
                                                : `Expense · ${selected.category}${
                                                      selected.subcategory
                                                          ? ` / ${selected.subcategory}`
                                                          : ''
                                                  }`
                                        }
                                    />
                                    <InfoRow
                                        label="Uploaded by"
                                        value={userName(selected.responsible_user_id)}
                                    />
                                    {selected.notes && (
                                        <InfoRow label="Notes" value={selected.notes} />
                                    )}
                                    <InfoRow
                                        label="File"
                                        value={selected.original_filename}
                                    />
                                    <InfoRow
                                        label="Uploaded"
                                        value={formatDate(
                                            selected.created_at,
                                            'MM/dd/yyyy HH:mm'
                                        )}
                                    />

                                    {/* Match Issues */}
                                    {receiptMatchIssues(selected, transaction).map(
                                        (issue, idx) => (
                                            <Typography
                                                key={idx}
                                                variant="body2"
                                                color="warning.main"
                                            >
                                                {issue}
                                            </Typography>
                                        )
                                    )}
                                </Stack>
                            </Stack>
                        )}
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 2, py: 1.5 }}>
                <Button onClick={onClose}>Cancel</Button>
                {showSaveEmployee && (
                    <Button
                        onClick={() => void handleSaveEmployee()}
                        disabled={busy || saving}
                    >
                        Save employee
                    </Button>
                )}
                {showMatch && (
                    <Button
                        variant="contained"
                        onClick={() => void handleMatch()}
                        disabled={busy || saving}
                    >
                        Match
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

ReceiptMatchDialog.displayName = 'ReceiptMatchDialog';

function InfoRow({ label, value }: { label: string; value: string }) {
    return (
        <Stack direction="row" spacing={1}>
            <Typography variant="body2" fontWeight={600} sx={{ minWidth: 100 }}>
                {label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {value}
            </Typography>
        </Stack>
    );
}
